import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  MultiSelect,
  NumberInput,
  Select,
  Text,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import React, { useCallback, useEffect, useState } from "react";
import {
  repositoryPos,
  repositoryWebOld,
} from "../../../../../_base/_const/_constVar";
import { ConditionObjectData } from "../../../../../model/TblPromotionConditionMultiple";

interface ItemSegmentModel {
  id: string;
  name: string;
  code: string;
}

interface MessageResponse<T> {
  data: T;
  success: boolean;
  message: string;
}

interface Category {
  categoryCode: string;
  categoryName: string;
  status: string;
  priorityStatus: string;
}

interface PromotionConditionModalProps {
  initialData: any;
  form: any;
  data?: string;
}

interface ItemProductSearchModel {
  itemCode: string;
  itemName: string;
  // ...other fields if needed...
}

const PromotionConditionModalV2: React.FC<PromotionConditionModalProps> = ({
  initialData,
  form,
  data,
}) => {
  const [conditions, setConditions] = useState<ConditionObjectData[]>([]);
  const [fieldUpdate, setFieldUpdate] = useState<string>("");

  const [typeProductOptions, setTypeProductOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [groupOptions, setGroupOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [brandOptions, setBrandOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [productSuggestions, setProductSuggestions] = useState<
    { value: string; label: string }[]
  >([]);

  // Debounce timers
  const [typeDebounceTimer, setTypeDebounceTimer] =
    useState<NodeJS.Timeout | null>(null);
  const [groupDebounceTimer, setGroupDebounceTimer] =
    useState<NodeJS.Timeout | null>(null);
  const [brandDebounceTimer, setBrandDebounceTimer] =
    useState<NodeJS.Timeout | null>(null);
  const [productDebounceTimer, setProductDebounceTimer] =
    useState<NodeJS.Timeout | null>(null);

  //Debounced search states
  const [categoryOptions, setCategoryOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [categorySearch, setCategorySearch] = useState("");
  const [categoryDebounceTimer, setCategoryDebounceTimer] =
    useState<NodeJS.Timeout | null>(null);
  const [productSearch, setProductSearch] = useState("");
  const [productDebounced, setProductDebounced] = useState("");

  const typeOptions = [
    { value: "quantity", label: "Số lượng" },
    { value: "price", label: "Giá tiền" },
    { value: "category", label: "Danh mục sản phẩm" },
    { value: "product", label: "Sản phẩm" },
    { value: "bundle", label: "Mua kèm" },
    { value: "type", label: "Loại" },
    { value: "group", label: "Nhóm" },
    { value: "brand", label: "Chủng" },
  ];

  const conditionTypeOptions = [
    { value: "equal", label: "Bằng" },
    { value: "greater", label: "Lớn hơn" },
    { value: "less", label: "Nhỏ hơn" },
    { value: "greaterEqual", label: "Lớn hơn hoặc bằng" },
    { value: "lessEqual", label: "Nhỏ hơn hoặc bằng" },
    { value: "range", label: "Trong khoảng" },
  ];

  const rewardTypeOptions = [
    { value: "discount", label: "Giảm giá" },
    { value: "gift", label: "Tặng kèm" },
  ];

  const discountTypeOptions = [
    { value: "percent", label: "Phần trăm (%)" },
    { value: "amount", label: "Tiền mặt (đ)" },
  ];

  const productOptions = [
    { value: "PRD001", label: "iPhone 15 Pro Max" },
    { value: "PRD002", label: "Samsung Galaxy S24" },
    { value: "PRD003", label: "MacBook Air M2" },
    { value: "PRD004", label: "iPad Pro 11" },
    { value: "PRD005", label: "AirPods Pro 2" },
  ];

  // Load data from API with search functionality
  const loadTypeData = async (keySearch = "") => {
    try {
      let typeUrl = `?Skip=0&Take=25&Type=3`;
      if (keySearch.trim()) {
        typeUrl += `&KeySearch=${keySearch.trim().toLowerCase()}`;
      }
      const typeResponse = await repositoryPos.get<
        MessageResponse<ItemSegmentModel[]>
      >(`/api/v1/Category/get-list${typeUrl}`);
      if (typeResponse && typeResponse.success) {
        setTypeProductOptions(
          typeResponse.data
            .filter((item) => !!(item.code || item.id))
            .map((item) => ({
              value: item.code || item.id,
              label: item.name,
            }))
        );
      }
    } catch (error) {
      console.error("Error loading type data:", error);
      setTypeProductOptions([]);
    }
  };

  const loadBrandData = async (keySearch = "") => {
    try {
      let brandUrl = `?Skip=0&Take=25&Type=4`;
      if (keySearch.trim()) {
        brandUrl += `&KeySearch=${keySearch.trim().toLowerCase()}`;
      }
      const brandResponse = await repositoryPos.get<
        MessageResponse<ItemSegmentModel[]>
      >(`/api/v1/Category/get-list${brandUrl}`);
      if (brandResponse && brandResponse.success) {
        setBrandOptions(
          brandResponse.data
            .filter((item) => !!(item.code || item.id))
            .map((item) => ({
              value: item.code || item.id,
              label: item.name,
            }))
        );
      }
    } catch (error) {
      console.error("Error loading brand data:", error);
      setBrandOptions([]);
    }
  };

  const loadGroupData = async (keySearch = "") => {
    try {
      let groupUrl = `?Skip=0&Take=25&Type=5`;
      if (keySearch.trim()) {
        groupUrl += `&KeySearch=${keySearch.trim().toLowerCase()}`;
      }
      const groupResponse = await repositoryPos.get<
        MessageResponse<ItemSegmentModel[]>
      >(`/api/v1/Category/get-list${groupUrl}`);
      if (groupResponse && groupResponse.success) {
        setGroupOptions(
          groupResponse.data
            .filter((item) => !!(item.code || item.id))
            .map((item) => ({
              value: item.code || item.id,
              label: item.name,
            }))
        );
      }
    } catch (error) {
      console.error("Error loading group data:", error);
      setGroupOptions([]);
    }
  };

  // Initial load
  useEffect(() => {
    // Load initial data without search
    loadTypeData();
    loadBrandData();
    loadGroupData();
  }, []);

  // Debounced search functions
  const debouncedLoadTypeData = useCallback(
    (keySearch: string) => {
      if (typeDebounceTimer) {
        clearTimeout(typeDebounceTimer);
      }

      const timer = setTimeout(() => {
        loadTypeData(keySearch);
      }, 300);

      setTypeDebounceTimer(timer);
    },
    [typeDebounceTimer]
  );

  const debouncedLoadGroupData = useCallback(
    (keySearch: string) => {
      if (groupDebounceTimer) {
        clearTimeout(groupDebounceTimer);
      }

      const timer = setTimeout(() => {
        loadGroupData(keySearch);
      }, 300);

      setGroupDebounceTimer(timer);
    },
    [groupDebounceTimer]
  );

  const debouncedLoadBrandData = useCallback(
    (keySearch: string) => {
      if (brandDebounceTimer) {
        clearTimeout(brandDebounceTimer);
      }

      const timer = setTimeout(() => {
        loadBrandData(keySearch);
      }, 300);

      setBrandDebounceTimer(timer);
    },
    [brandDebounceTimer]
  );

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (typeDebounceTimer) clearTimeout(typeDebounceTimer);
      if (groupDebounceTimer) clearTimeout(groupDebounceTimer);
      if (brandDebounceTimer) clearTimeout(brandDebounceTimer);
    };
  }, [typeDebounceTimer, groupDebounceTimer, brandDebounceTimer]);

  // Debounce product search value
  useEffect(() => {
    if (productDebounceTimer) clearTimeout(productDebounceTimer);
    const timer = setTimeout(() => {
      setProductDebounced(productSearch);
    }, 300);
    setProductDebounceTimer(timer);
    return () => clearTimeout(timer);
  }, [productSearch]);

  // Fetch product/bundle suggestions when needed
  useEffect(() => {
    // Determine if any condition is product or bundle and get the current search value
    const activeType = conditions?.find(
      (c) => c.type === "product" || c.type === "bundle"
    );
    if (activeType && productDebounced && productDebounced.length > 2) {
      const fetchItemSell = async () => {
        const keySearch = productDebounced.toLowerCase();
        const url = `/api-web/api/v1/TblItem/get-list-v2?KeySearch=${keySearch}&Skip=0&Take=10`;
        try {
          const searchResult = await repositoryWebOld.get<
            MessageResponse<ItemProductSearchModel[]>
          >(url);
          setProductSuggestions(
            searchResult?.data
              ? searchResult.data
                  .filter((item) => !!item.itemCode)
                  .map((item) => ({
                    value: item.itemCode,
                    label: `${item.itemName} (${item.itemCode})`,
                  }))
              : []
          );
        } catch {
          setProductSuggestions([]);
        }
      };
      fetchItemSell();
    } else {
      setProductSuggestions([]);
    }
  }, [productDebounced, conditions]);

  const addCondition = () => {
    setConditions((prev) => [
      ...prev,
      {
        type: initialData,
        conditionType: "equal",
        value1: 0,
        discountType: "percent",
        discountValue: 0,
        rewardType: "discount",
      },
    ]);
  };

  const loadCategoryOptions = async (keySearch = "") => {
    const url = `/api-web/api/v1/TblCategory/get-list-v2?Status=A&PriorityStatus=Y&KeySearch=${encodeURIComponent(
      keySearch
    )}&Skip=0&Take=10`;
    try {
      const result = await repositoryWebOld.get<MessageResponse<Category[]>>(
        url
      );
      if (result?.data && Array.isArray(result.data)) {
        setCategoryOptions(
          result.data
            .filter((item) => !!item.categoryCode)
            .map((item) => ({
              value: item.categoryCode,
              label: item.categoryName,
            }))
        );
      }
    } catch {
      setCategoryOptions([]);
    }
  };

  useEffect(() => {
    if (categoryDebounceTimer) clearTimeout(categoryDebounceTimer);
    const timer = setTimeout(() => {
      loadCategoryOptions(categorySearch);
    }, 300);
    setCategoryDebounceTimer(timer);
    return () => clearTimeout(timer);
  }, [categorySearch]);

  useEffect(() => {
    loadCategoryOptions("");
  }, []);

  const removeCondition = (index: number) => {
    setConditions((prev) => prev.filter((_, i) => i !== index));
  };

  const updateCondition = (
    index: number,
    field: keyof ConditionObjectData,
    value: any
  ) => {
    setConditions((prev) =>
      prev.map((condition, i) => {
        if (i === index) {
          const updatedCondition = { ...condition, [field]: value };

          // Auto-set defaults for category, product, and bundle types
          if (
            field === "type" &&
            [
              "category",
              "product",
              "bundle",
              "type",
              "group",
              "brand",
            ].includes(value)
          ) {
            updatedCondition.conditionType = "equal";
            updatedCondition.value1 = 1;
          }

          return updatedCondition;
        }
        return condition;
      })
    );
  };

  // const handleSave = () => {
  //   // Nếu chỉ có 1 điều kiện, trả về single object
  //   // Nếu có nhiều điều kiện, trả về array
  //   if (conditions.length === 1) {
  //     onSave((prev: any) =>
  //       prev.map((item: any) =>
  //         String(item.id) === String(initialData?.id)
  //           ? {
  //               ...item,
  //               conditionObject: JSON.stringify({
  //                 conditions: [conditions[0]],
  //               }),
  //               conditionLabel: getConditionPreview(conditions[0]),
  //             }
  //           : item
  //       )
  //     );
  //     modals.closeAll();
  //   } else {
  //     onSave((prev: any) =>
  //       prev.map((item: any) =>
  //         String(item.id) === String(initialData?.id)
  //           ? {
  //               ...item,
  //               conditionObject: JSON.stringify({ conditions: [conditions] }),
  //               conditionLabel: getConditionPreview(conditions[0]),
  //             }
  //           : item
  //       )
  //     );
  //     modals.closeAll();
  //   }
  // };

  const isRangeCondition = (conditionType: string) => conditionType === "range";
  const isSingleValueCondition = (conditionType: string) =>
    ["equal", "greater", "less", "greaterEqual", "lessEqual"].includes(
      conditionType
    );
  const shouldShowConditionType = (type: string) => {
    return ![
      "category",
      "product",
      "bundle",
      "type",
      "group",
      "brand",
    ].includes(type);
  };
  const shouldShowValueInputs = (type: string) => {
    return ["quantity", "price"].includes(type);
  };

  const getConditionPreview = (condition: ConditionObjectData) => {
    const typeLabels: Record<string, string> = {
      quantity: "số lượng",
      price: "giá tiền",
      category: "danh mục",
      product: "sản phẩm",
      bundle: "mua kèm",
    };

    const conditionLabels: Record<string, string> = {
      equal: "bằng",
      greater: "lớn hơn",
      less: "nhỏ hơn",
      greaterEqual: "lớn hơn hoặc bằng",
      lessEqual: "nhỏ hơn hoặc bằng",
      range: "từ",
    };

    let preview = "";

    switch (condition.type) {
      case "quantity":
      case "price":
        if (condition.conditionType === "range") {
          preview = `Mua ${typeLabels[condition.type]} ${
            conditionLabels[condition.conditionType]
          } ${condition.value1?.toLocaleString()} đến ${condition.value2?.toLocaleString()}`;
          if (condition.type === "price") preview += "đ";
        } else {
          preview = `Mua ${typeLabels[condition.type]} ${
            conditionLabels[condition.conditionType || "equal"]
          } ${condition.value1?.toLocaleString()}`;
          if (condition.type === "price") preview += "đ";
        }
        break;
      case "category":
        const categoryName =
          condition.categoryText ||
          categoryOptions.find((cat) => cat.value === condition.categoryCode)
            ?.label ||
          condition.categoryCode ||
          "chưa chọn";
        preview = `Mua cùng sản phẩm thuộc danh mục: ${categoryName}`;
        break;
      case "product":
        const productName =
          condition.categoryText ||
          productOptions.find((prod) => prod.value === condition.categoryCode)
            ?.label ||
          condition.categoryCode ||
          "chưa chọn";
        preview = `Mua cùng sản phẩm: ${productName}`;
        break;
      case "bundle":
        const bundleName =
          condition.categoryText ||
          productOptions.find((prod) => prod.value === condition.categoryCode)
            ?.label ||
          condition.categoryCode ||
          "chưa chọn";
        preview = `Mua cùng sản phẩm: ${bundleName}`;
        break;
      case "type":
        const typeName =
          condition.categoryText ||
          typeProductOptions.find(
            (type) => type.value === condition.categoryCode
          )?.label ||
          condition.categoryCode ||
          "chưa chọn";
        preview = `Mua cùng sản phẩm loại: ${typeName}`;
        break;
      case "group":
        const groupName =
          condition.categoryText ||
          groupOptions.find((group) => group.value === condition.categoryCode)
            ?.label ||
          condition.categoryCode ||
          "chưa chọn";
        preview = `Mua cùng sản phẩm nhóm: ${groupName}`;
        break;
      case "brand":
        const brandName =
          condition.categoryText ||
          brandOptions.find((brand) => brand.value === condition.categoryCode)
            ?.label ||
          condition.categoryCode ||
          "chưa chọn";
        preview = `Mua cùng sản phẩm chủng: ${brandName}`;
        break;
    }

    preview += ` → ${
      condition.rewardType === "gift" ? "Tặng kèm" : "Giảm giá"
    } `;
    preview +=
      condition.discountType === "percent"
        ? `${condition.discountValue}%`
        : `${condition.discountValue?.toLocaleString()}đ`;

    return preview;
  };

  useEffect(() => {
    form.setFieldValue(
      `${fieldUpdate}`,
      Array.isArray(conditions) && conditions.length > 0
        ? JSON.stringify(conditions)
        : null
    );
  }, [conditions]);

  useEffect(() => {
    if (initialData === "quantity") {
      setFieldUpdate("attribute15");
    } else if (initialData === "price") {
      setFieldUpdate("attribute14");
    } else if (initialData === "category") {
      setFieldUpdate("attribute13");
    } else if (initialData === "bundle") {
      setFieldUpdate("attribute12");
    } else if (initialData === "type") {
      setFieldUpdate("attribute11");
    } else if (initialData === "group") {
      setFieldUpdate("attribute10");
    } else if (initialData === "brand") {
      setFieldUpdate("attribute9");
    }
  }, [initialData]);

  useEffect(() => {
    if (data && data.length > 0) {
      setConditions(JSON.parse(data));
    }
  }, [data]);

  return (
    <Box>
      <Box
        style={{
          position: "sticky",
          top: 0,
          backgroundColor: "white",
          zIndex: 10,
          borderBottom: "1px solid #e9ecef",
          marginBottom: "16px",
        }}
      >
        <Flex justify="space-between" align="center">
          <Text size="sm" c="dimmed">
            Thiết lập các điều kiện cho chính sách khuyến mãi
          </Text>
          <Button
            leftSection={<IconPlus size={16} />}
            variant="light"
            onClick={addCondition}
            my="lg"
          >
            Thêm điều kiện
          </Button>
          {/* <Flex gap="md">
            <Button
              variant="outline"
              onClick={() => modals.closeAll()}
              size="sm"
            >
              Hủy
            </Button>
            <Button
              // onClick={handleSave}
              size="sm"
            >
              Lưu điều kiện
            </Button>
          </Flex> */}
        </Flex>
      </Box>

      <Grid>
        {Array.isArray(conditions) &&
          conditions.length > 0 &&
          conditions.map((condition, index) => (
            <Grid.Col span={{ base: 12, md: 6, lg: 6 }} key={index}>
              {/* <Grid.Col span={12} key={index}> */}
              <Box
                key={index}
                mb="md"
                p="md"
                style={{
                  border: "1px solid #d0d7de",
                  borderRadius: "8px",
                  backgroundColor: "white",
                }}
              >
                <Flex justify="space-between" align="center" mb={2.5}>
                  <Badge color="blue">Điều kiện {index + 1}</Badge>
                  <ActionIcon
                    color="red"
                    variant="subtle"
                    onClick={() => removeCondition(index)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Flex>

                <Grid>
                  <Grid.Col span={4}>
                    <Select
                      label="Loại điều kiện"
                      data={typeOptions}
                      value={condition.type}
                      variant="filled"
                      onChange={(value) =>
                        updateCondition(index, "type", value)
                      }
                      size="sm"
                      readOnly
                    />
                  </Grid.Col>

                  {shouldShowConditionType(condition.type) && (
                    <Grid.Col span={4}>
                      <Select
                        label="Kiểu so sánh"
                        data={conditionTypeOptions}
                        value={condition.conditionType}
                        onChange={(value) =>
                          updateCondition(index, "conditionType", value)
                        }
                        withAsterisk
                        size="sm"
                      />
                    </Grid.Col>
                  )}

                  {shouldShowValueInputs(condition.type) &&
                    isSingleValueCondition(condition.conditionType) && (
                      <Grid.Col span={4}>
                        <NumberInput
                          label={
                            condition.type === "price"
                              ? "Giá trị (đ)"
                              : "Số lượng"
                          }
                          value={condition.value1}
                          onChange={(value) =>
                            updateCondition(index, "value1", value || 0)
                          }
                          withAsterisk
                          min={0}
                          size="sm"
                          thousandSeparator=","
                          decimalSeparator="."
                        />
                      </Grid.Col>
                    )}

                  {shouldShowValueInputs(condition.type) &&
                    isRangeCondition(condition.conditionType) && (
                      <>
                        <Grid.Col span={2}>
                          <NumberInput
                            label={condition.type === "price" ? "Từ (đ)" : "Từ"}
                            value={condition.value1}
                            onChange={(value) =>
                              updateCondition(index, "value1", value || 0)
                            }
                            withAsterisk
                            min={0}
                            size="sm"
                            thousandSeparator=","
                            decimalSeparator="."
                          />
                        </Grid.Col>
                        <Grid.Col span={2}>
                          <NumberInput
                            label={
                              condition.type === "price" ? "Đến (đ)" : "Đến"
                            }
                            value={condition.value2 || 0}
                            onChange={(value) =>
                              updateCondition(index, "value2", value || 0)
                            }
                            withAsterisk
                            min={condition.value1 || 0}
                            size="sm"
                            thousandSeparator=","
                            decimalSeparator="."
                          />
                        </Grid.Col>
                      </>
                    )}

                  {condition.type === "category" && (
                    <Grid.Col span={8}>
                      <Select
                        label="Danh mục sản phẩm"
                        data={categoryOptions}
                        value={String(condition.categoryCode) ?? ""}
                        onChange={(value) => {
                          const selectedItem = categoryOptions.find(
                            (item) => item.value === value
                          );
                          updateCondition(index, "categoryCode", value);
                          updateCondition(
                            index,
                            "categoryText",
                            selectedItem?.label || ""
                          );
                        }}
                        placeholder="Chọn danh mục hoặc nhập mã"
                        searchable
                        size="sm"
                        onSearchChange={setCategorySearch}
                      />
                    </Grid.Col>
                  )}

                  {/* {condition.type === "product" && (
                    <Grid.Col span={8}>
                      <Select
                        label="Sản phẩm"
                        data={
                          // Ensure selected value is always present in data
                          condition.categoryCode &&
                          !productSuggestions.some(
                            (item) => item.value === condition.categoryCode
                          )
                            ? [
                                ...productSuggestions,
                                {
                                  value: condition.categoryCode,
                                  label:
                                    condition.categoryText ||
                                    condition.categoryCode,
                                },
                              ]
                            : productSuggestions
                        }
                        value={condition.categoryCode || ""}
                        onChange={(value) => {
                          const selectedItem = productSuggestions.find(
                            (item) => item.value === value
                          );
                          updateCondition(index, "categoryCode", value);
                          updateCondition(
                            index,
                            "categoryText",
                            selectedItem?.label || ""
                          );
                        }}
                        placeholder="Chọn sản phẩm hoặc nhập mã"
                        searchable
                        size="sm"
                        onSearchChange={setProductSearch}
                        searchValue={productSearch}
                        nothingFoundMessage="Không tìm thấy sản phẩm"
                      />
                    </Grid.Col>
                  )} */}

                  {condition.type === "bundle" && (
                    <Grid.Col span={8}>
                      <MultiSelect
                        label="Sản phẩm mua kèm"
                        data={productSuggestions}
                        value={
                          condition.categoryCode &&
                          Array.isArray(condition.categoryCode) &&
                          condition.categoryCode?.length > 0
                            ? condition.categoryCode
                            : []
                        }
                        onChange={(value) => {
                          updateCondition(index, "categoryCode", value);
                          updateCondition(
                            index,
                            "categoryText",
                            value.join(",")
                          );
                        }}
                        placeholder="Chọn sản phẩm hoặc nhập mã"
                        searchable
                        size="sm"
                        onSearchChange={setProductSearch}
                        searchValue={productSearch}
                        nothingFoundMessage="Nhập từ khóa để tìm kiếm !"
                      />
                    </Grid.Col>
                  )}

                  {condition.type === "type" && (
                    <Grid.Col span={8}>
                      <MultiSelect
                        label="Loại sản phẩm"
                        data={typeProductOptions}
                        value={
                          condition.categoryCode &&
                          Array.isArray(condition.categoryCode) &&
                          condition.categoryCode?.length > 0
                            ? condition.categoryCode
                            : []
                        }
                        onChange={(value) => {
                          updateCondition(index, "categoryCode", value);
                          updateCondition(
                            index,
                            "categoryText",
                            value.join(",")
                          );
                        }}
                        placeholder="Chọn loại sản phẩm hoặc nhập mã"
                        searchable
                        size="sm"
                        onSearchChange={(query) => {
                          debouncedLoadTypeData(query);
                        }}
                        searchValue={undefined}
                      />
                    </Grid.Col>
                  )}

                  {condition.type === "group" && (
                    <Grid.Col span={8}>
                      <MultiSelect
                        label="Nhóm sản phẩm"
                        data={groupOptions}
                        value={
                          condition.categoryCode &&
                          Array.isArray(condition.categoryCode) &&
                          condition.categoryCode?.length > 0
                            ? condition.categoryCode
                            : []
                        }
                        onChange={(value) => {
                          updateCondition(index, "categoryCode", value);
                          updateCondition(
                            index,
                            "categoryText",
                            value.join(",")
                          );
                        }}
                        placeholder="Chọn nhóm sản phẩm hoặc nhập mã"
                        searchable
                        size="sm"
                        onSearchChange={(query) => {
                          debouncedLoadGroupData(query);
                        }}
                        searchValue={undefined}
                      />
                    </Grid.Col>
                  )}

                  {condition.type === "brand" && (
                    <Grid.Col span={8}>
                      <MultiSelect
                        label="Chủng sản phẩm"
                        data={brandOptions}
                        value={
                          condition.categoryCode &&
                          Array.isArray(condition.categoryCode) &&
                          condition.categoryCode?.length > 0
                            ? condition.categoryCode
                            : []
                        }
                        onChange={(value) => {
                          updateCondition(index, "categoryCode", value);
                          updateCondition(
                            index,
                            "categoryText",
                            value.join(",")
                          );
                        }}
                        placeholder="Chọn chủng hoặc nhập mã"
                        searchable
                        size="sm"
                        onSearchChange={(query) => {
                          debouncedLoadBrandData(query);
                        }}
                        searchValue={undefined}
                      />
                    </Grid.Col>
                  )}

                  <Grid.Col span={4}>
                    <Select
                      label="Kiểu giảm giá"
                      data={discountTypeOptions}
                      value={condition.discountType}
                      onChange={(value) =>
                        updateCondition(index, "discountType", value)
                      }
                      withAsterisk
                      size="sm"
                    />
                  </Grid.Col>

                  <Grid.Col span={4}>
                    <NumberInput
                      label={
                        condition.discountType === "percent"
                          ? "Giảm (%)"
                          : "Giảm (đ)"
                      }
                      value={condition.discountValue}
                      onChange={(value) =>
                        updateCondition(index, "discountValue", value || 0)
                      }
                      withAsterisk
                      min={condition.discountType === "percent" ? 0.01 : 0}
                      max={
                        condition.discountType === "percent" ? 100 : undefined
                      }
                      step={condition.discountType === "percent" ? 0.01 : 1}
                      size="sm"
                      thousandSeparator=","
                      decimalSeparator="."
                    />
                  </Grid.Col>
                </Grid>

                <Divider my="md" />

                <Box
                  p="sm"
                  style={{
                    backgroundColor: "#f6f8fa",
                    borderRadius: "6px",
                    borderLeft: "3px solid #0969da",
                  }}
                >
                  <Text size="sm" fw={500} mb="xs" c="dark">
                    Xem trước điều kiện:
                  </Text>
                  <Text size="sm" c="dimmed">
                    {getConditionPreview(condition)}
                  </Text>
                </Box>
              </Box>
            </Grid.Col>
          ))}
      </Grid>
    </Box>
  );
};

export default PromotionConditionModalV2;
